import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API } from "../lib/brand";
import { toast } from "sonner";
import { usePageUpdateGuard } from "../hooks/usePageUpdateGuard";

const ROLES = [
  "CEO / Founder",
  "CFO",
  "CRO / Head of Risk",
  "COO / Head of Operations",
  "CIO / Head of Strategy",
  "CTO / Head of Engineering",
  "Chief Compliance Officer",
  "Head of Policy or Regulation",
  "Board member / Trustee",
  "Other",
];

const INDUSTRIES = [
  "Banking",
  "Sovereign Wealth",
  "Real Estate",
  "Insurance",
  "Healthcare",
  "Energy",
  "Manufacturing",
  "Public Sector",
  "Telecommunications",
  "Retail",
  "Maritime",
  "Defense",
  "Space",
  "Robotics",
  "Physical AI",
  "Other",
];

const DECISIONS = [
  "Multi-year capital allocation",
  "Pricing or credit policy",
  "Risk or scenario rehearsal",
  "Regulatory or compliance posture",
  "Process or capability redesign",
  "Investment programme review",
  "Other",
];

const initial = {
  name: "",
  email: "",
  company: "",
  role: "",
  industry: "",
  decision: "",
  decisionOther: "",
  message: "",
  consent: false,
};

export default function DemoDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  usePageUpdateGuard(open || submitting || (!done && Object.values(form).some(Boolean)));

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.company) {
      toast.error("Name, email and company are required.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent before sending.");
      return;
    }
    setSubmitting(true);
    try {
      // Resolve the "Other" decision case
      const decisionFinal =
        form.decision === "Other" && form.decisionOther
          ? form.decisionOther.trim()
          : form.decision;

      const payload = {
        name: form.name,
        email: form.email,
        company: form.company,
        role: form.role,
        industry: form.industry,
        decision: decisionFinal,
        message: form.message,
        consent: form.consent,
      };

      const res = await fetch(`${API}/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Request failed");
      setDone(true);
      toast.success("Your request has been recorded.");
    } catch (err) {
      toast.error("Could not submit. Please try again or email hello@heliosbrain.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setForm(initial); setDone(false); }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent
        data-testid="demo-dialog"
        className="bg-[#141414] border border-white/10 text-white max-w-2xl p-0 rounded-lg max-h-[92vh] overflow-y-auto"
      >
        <div className="p-8 md:p-12">
          <DialogHeader>
            <div className="eyebrow-flame mb-4" data-testid="demo-eyebrow">Your next move</div>
            <DialogTitle data-testid="demo-title" className="font-display font-normal text-3xl md:text-4xl leading-tight">
              {done ? "Your request is recorded." : "What are you trying to achieve?"}
            </DialogTitle>
            <DialogDescription data-testid="demo-description" className="text-zinc-400 mt-3 leading-relaxed">
              {done
                ? "Your details have been saved. You can also contact our team directly at hello@heliosbrain.com."
                : "Tell us your objective and what stands in the way. We will explore where Helios could help."}
            </DialogDescription>
          </DialogHeader>

          {!done && (
            <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="md:col-span-1">
                <label htmlFor="demo-name" className="eyebrow block mb-2">Name</label>
                <input id="demo-name" data-testid="demo-input-name" className="hb-input" value={form.name} onChange={update("name")} placeholder="Your full name" required />
              </div>
              <div className="md:col-span-1">
                <label htmlFor="demo-email" className="eyebrow block mb-2">Email</label>
                <input id="demo-email" data-testid="demo-input-email" type="email" className="hb-input" value={form.email} onChange={update("email")} placeholder="name@institution.com" required />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="demo-company" className="eyebrow block mb-2">Company</label>
                <input id="demo-company" data-testid="demo-input-company" className="hb-input" value={form.company} onChange={update("company")} placeholder="Organization name" required />
              </div>

              <div className="md:col-span-1">
                <label htmlFor="demo-role" className="eyebrow block mb-2">Role</label>
                <select
                  id="demo-role"
                  data-testid="demo-input-role"
                  className="hb-input"
                  value={form.role}
                  onChange={update("role")}
                >
                  <option value="">Choose a role</option>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div className="md:col-span-1">
                <label htmlFor="demo-industry" className="eyebrow block mb-2">Industry</label>
                <select
                  id="demo-industry"
                  data-testid="demo-input-industry"
                  className="hb-input"
                  value={form.industry}
                  onChange={update("industry")}
                >
                  <option value="">Choose an industry</option>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="demo-decision" className="eyebrow block mb-2">What kind of decision?</label>
                <select
                  id="demo-decision"
                  data-testid="demo-input-decision"
                  className="hb-input"
                  value={form.decision}
                  onChange={update("decision")}
                >
                  <option value="">Choose the kind of decision</option>
                  {DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              {form.decision === "Other" && (
                <div className="md:col-span-2">
                  <label htmlFor="demo-other" className="eyebrow block mb-2">Describe the decision</label>
                  <input
                    id="demo-other"
                    data-testid="demo-input-decision-other"
                    className="hb-input"
                    value={form.decisionOther}
                    onChange={update("decisionOther")}
                    placeholder="In one sentence"
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="eyebrow block mb-2" htmlFor="demo-message">What would a better outcome look like? (optional)</label>
                <textarea id="demo-message" data-testid="demo-input-message" className="hb-input hb-textarea" value={form.message} onChange={update("message")} placeholder="Your objective, constraints and timing." />
              </div>

              <div className="md:col-span-2">
                <label
                  className="flex items-start gap-3 cursor-pointer select-none"
                  htmlFor="demo-consent"
                  data-testid="demo-consent-row"
                >
                  <input
                    id="demo-consent"
                    data-testid="demo-input-consent"
                    type="checkbox"
                    checked={form.consent}
                    onChange={update("consent")}
                    className="mt-1 w-4 h-4 accent-[#FF6600] cursor-pointer"
                    required
                  />
                  <span className="text-zinc-400 leading-relaxed" style={{ fontSize: 13 }}>
                    I consent to Helios Brain processing these details to respond to my enquiry,
                    as described in the <a data-testid="demo-privacy-link" href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">privacy policy</a>. This does not subscribe me to marketing.
                  </span>
                </label>
              </div>

              <div className="md:col-span-2 mt-2 flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-4">
                <div className="text-xs text-zinc-400" data-testid="demo-response-note">
                  A conversation about your real-world challenge.
                </div>
                <button
                  data-testid="demo-submit"
                  type="submit"
                  disabled={submitting}
                  className="hb-btn-primary disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send request"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </button>
              </div>
            </form>
          )}

          {done && (
            <div className="mt-10">
              <button data-testid="demo-close" onClick={close} className="hb-btn-secondary">Close</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
